import { THEME_DEFINITIONS, getThemeDefinition } from '../data'
import type {
  AlbumConfig,
  AlbumRecord,
  AppState,
  InvestmentDestination,
  StickerInvestment
} from '../types'
import { normalizeState } from '../../../shared/persistence'
import type { PersistedState } from '../../../shared/persistence'

export const ALBUM_PAGE_SIZE_OPTIONS = [25, 50, 100] as const

export function normalizeThemeKey(key: string): AlbumConfig['theme'] {
  const legacyMap: Record<string, AlbumConfig['theme']> = {
    soccer: 'soccer50',
    soccer100: 'soccer50',
    soccer250: 'soccer50',
    soccer350: 'soccer50',
    soccer500: 'soccer50',
    pokemon: 'pokemon500',
    mtg: 'mtg500',
    tourist: 'tourist326',
    rockroll: 'rockroll432'
  }
  const mapped = legacyMap[key] ?? key
  if (mapped in THEME_DEFINITIONS) return mapped as AlbumConfig['theme']
  return 'standard50'
}

export function normalizeAlbumRecordTheme(record: AlbumRecord): AlbumRecord {
  const normalizedTheme = normalizeThemeKey(record.config.theme)
  const size = getThemeDefinition(normalizedTheme).size
  return {
    ...record,
    config: {
      ...record.config,
      theme: normalizedTheme,
      size
    }
  }
}

export function appStateFromPersisted(persisted: PersistedState): AppState {
  return {
    albums: persisted.albums,
    activeAlbumId: persisted.activeAlbumId
  }
}

export function persistedFromAppState(state: AppState): PersistedState {
  return normalizeState({
    albums: state.albums,
    activeAlbumId: state.activeAlbumId
  })
}

export function getAvailableAlbumPageSizes(albumSize: number): readonly number[] {
  return ALBUM_PAGE_SIZE_OPTIONS.filter((size) => size <= albumSize)
}

export function resolveAlbumPageSize(preferredSize: number, albumSize?: number): number {
  const availableSizes =
    typeof albumSize === 'number' ? getAvailableAlbumPageSizes(albumSize) : ALBUM_PAGE_SIZE_OPTIONS

  if (availableSizes.includes(preferredSize)) {
    return preferredSize
  }

  const smallerOrEqual = [...availableSizes].reverse().find((size) => size <= preferredSize)
  if (smallerOrEqual) {
    return smallerOrEqual
  }

  return availableSizes[0] ?? albumSize ?? ALBUM_PAGE_SIZE_OPTIONS[0]
}

export function selectAlbumState(state: AppState, albumId: string): AppState {
  return { ...state, activeAlbumId: albumId }
}

export function createAlbumState(state: AppState, record: AlbumRecord): AppState {
  const normalized = normalizeAlbumRecordTheme(record)
  return {
    albums: [...state.albums, normalized],
    activeAlbumId: normalized.config.id
  }
}

export function deleteAlbumState(state: AppState, albumId: string): AppState {
  const nextAlbums = state.albums.filter((a) => a.config.id !== albumId)
  const nextActiveId =
    state.activeAlbumId === albumId ? (nextAlbums[0]?.config.id ?? null) : state.activeAlbumId
  return { albums: nextAlbums, activeAlbumId: nextActiveId }
}

export function backToSelectorState(state: AppState): AppState {
  return { ...state, activeAlbumId: null }
}

export function updateActiveRecordState(
  state: AppState,
  updater: (prev: AlbumRecord) => AlbumRecord
): AppState {
  return {
    ...state,
    albums: state.albums.map((a) => (a.config.id === state.activeAlbumId ? updater(a) : a))
  }
}

export function updateAlbumRecord(
  prev: AlbumRecord,
  key: keyof AlbumConfig,
  value: AlbumConfig[keyof AlbumConfig]
): AlbumRecord {
  return {
    ...prev,
    config: { ...prev.config, [key]: value, updatedAt: new Date().toISOString() }
  }
}

export function addDestinationRecord(prev: AlbumRecord, dest: InvestmentDestination): AlbumRecord {
  return {
    ...prev,
    config: {
      ...prev.config,
      savedInvestments: [...prev.config.savedInvestments, dest],
      updatedAt: new Date().toISOString()
    }
  }
}

export function removeDestinationRecord(prev: AlbumRecord, destId: string): AlbumRecord {
  const nextSavedInvestments = prev.config.savedInvestments.filter((d) => d.id !== destId)
  const nextStickers = Object.fromEntries(
    Object.entries(prev.stickers).filter(([, s]) => s.destinationId !== destId)
  ) as Record<number, StickerInvestment>
  return {
    config: {
      ...prev.config,
      savedInvestments: nextSavedInvestments,
      updatedAt: new Date().toISOString()
    },
    stickers: nextStickers
  }
}

export function pruneStickersToSizeRecord(prev: AlbumRecord, size: number): AlbumRecord {
  const excess = Object.keys(prev.stickers).filter((k) => Number(k) > size)
  if (excess.length === 0) return prev
  const nextStickers = { ...prev.stickers }
  excess.forEach((k) => {
    delete nextStickers[Number(k)]
  })
  return { ...prev, stickers: nextStickers }
}

export function saveStickerInvestmentRecord(
  prev: AlbumRecord,
  stickerNumber: number,
  destinationId: string,
  comments: string
): AlbumRecord {
  return {
    ...prev,
    stickers: {
      ...prev.stickers,
      [stickerNumber]: {
        stickerNumber,
        albumId: prev.config.id,
        destinationId,
        comments: comments.trim(),
        investedAt: prev.stickers[stickerNumber]?.investedAt ?? new Date().toISOString()
      }
    }
  }
}

export function removeStickerInvestmentRecord(
  prev: AlbumRecord,
  stickerNumber: number
): AlbumRecord {
  const next = { ...prev.stickers }
  delete next[stickerNumber]
  return { ...prev, stickers: next }
}
